import { useQuery } from "@tanstack/react-query";
import { testingAxios, testingAuthHeaders } from "@/services/testing";
import { RUNS_ENDPOINT } from "@/services/testing";
import type { TestRun } from "@/services/testing";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

// Must exceed the server's stale-run reaper (expireStaleActiveForPlan marks a run 'error' after
// ~30 min, on each runs-list poll). If this window equalled 30 min, the client would stop polling at
// the exact moment the reap happens — so the still-"running" run would silently vanish from the UI
// (Stop button + reconnect banner gone) without ever showing its terminal state. Keeping the client
// polling a few minutes longer guarantees it observes the run either finishing OR being reaped to
// 'error', which then surfaces in Run history instead of disappearing.
const ACTIVE_RUN_RECONNECT_WINDOW_MS = 35 * 60 * 1000;

async function fetchRuns(planId: string, limit: number): Promise<TestRun[]> {
  const r = await testingAxios.get<{ runs: TestRun[]; count: number }>(RUNS_ENDPOINT, {
    headers: testingAuthHeaders(),
    params: { plan_id: planId, limit },
  });
  return r.data.runs;
}

export function useRuns(planId: string | null, limit = 20) {
  const token = useAuthStoreV1((s) => s.token);
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const userId = useAuthStoreV1((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: ["testingRuns", planId, orgId],
    queryFn: () => fetchRuns(planId!, limit),
    enabled: !!(token && orgId && userId && planId),
    refetchOnWindowFocus: false,
    // This is a background reconnect poll — a transient failure (the testing
    // service is busy driving a live browser run) must NOT raise the global
    // "An unexpected error occurred" toast. It self-heals on the next poll.
    meta: { silent: true },
    retry: 3,
    // Refresh-resilience: a run keeps executing on the server even if the live
    // SSE stream was dropped (tab switch / reload). Poll while any run is
    // active so the UI reconnects to it and updates to its final status.
    // Poll faster while a run is active: this same list feeds the reconnect step feed (it carries the
    // active run's summary.live_events), so a tighter cadence makes steps resume snappily after a tab
    // switch. Idle plans don't poll at all.
    refetchInterval: (query) => {
      // Keep polling THROUGH a transient error: on a tab-switch-back mid-run the
      // first fetch can fail while the service is busy, leaving no data. Without
      // this the interval would stop (no data → not "active") and the run would
      // never reconnect (no live feed, no Stop button). A short poll on error
      // recovers it. Capped so a persistently-failing plan doesn't poll forever.
      if (query.state.status === "error") {
        return query.state.errorUpdateCount <= 20 ? 3000 : false;
      }
      const runs = query.state.data as TestRun[] | undefined;
      const active = runs?.some(isFreshActiveRun);
      return active ? 2500 : false;
    },
  });
}

export function isFreshActiveRun(run: TestRun): boolean {
  if (run.status !== "running" && run.status !== "queued") return false;
  const startedAt =
    typeof run.started_at === "string"
      ? Date.parse(run.started_at)
      : run.started_at
        ? Date.parse(String(run.started_at))
        : Number.NaN;
  if (!Number.isFinite(startedAt)) return false;
  return Date.now() - startedAt < ACTIVE_RUN_RECONNECT_WINDOW_MS;
}
